/**
 * The mobile-number SMS code at sign-up (docs/adr/0036-signup-verification.md).
 *
 * Firebase Auth sends the SMS and checks the code; what comes out is Firebase's signed ID token, which
 * `register` takes as `phoneProof` and verifies on the server. Nothing else of Firebase is used: the
 * Firebase "user" exists only to mint that token, in memory (no persistence), and is signed out right
 * after.
 *
 * The SDK is imported on demand, so it costs nothing to anyone who never reaches the mobile step.
 * The browser uses the web SDK (invisible reCAPTCHA). The Android app uses the phone's own Firebase SDK
 * behind this same `PhoneVerifier` interface (`nativePhoneVerifier.ts`, Play Integrity and SMS
 * auto-read), because the reCAPTCHA does not pass inside the Android WebView. An older installed app
 * has no native plugin and keeps using the web SDK.
 */

import type { SignupOptions } from '@hrms/shared';

export type PhoneVerificationConfig = NonNullable<SignupOptions['phoneVerification']>;

export interface PhoneCodeSession {
  /** Resolves to the proof for `register`; rejects with a PhoneVerificationError. */
  confirm(code: string): Promise<string>;
}

/**
 * What the Android app can do without the person typing: the phone read the code out of the SMS
 * (`onCode`), or Firebase verified the number on its own (`onProof`, no code at all). Only the native
 * verifier calls these; the web one ignores them.
 */
export interface PhoneAutoHandlers {
  onCode?(code: string): void;
  onProof?(proof: string): void;
  /** A failure that arrives after `send` has already returned (the auto path failing late). */
  onFailure?(error: PhoneVerificationError): void;
}

export interface PhoneVerifier {
  /** Texts a code to an E.164 number. Rejects with a PhoneVerificationError. */
  send(phone: string, handlers?: PhoneAutoHandlers): Promise<PhoneCodeSession>;
  dispose(): void;
}

export type PhoneVerificationFailure =
  | 'invalid_number'
  | 'invalid_code'
  | 'code_expired'
  | 'too_many_requests'
  | 'unavailable'
  | 'captcha'
  | 'network'
  | 'unknown';

export class PhoneVerificationError extends Error {
  constructor(readonly reason: PhoneVerificationFailure) {
    super(phoneVerificationMessage(reason));
    this.name = 'PhoneVerificationError';
  }
}

/** Firebase's error codes, folded into the handful of things the person can act on differently. */
export function phoneVerificationFailure(code: string | undefined): PhoneVerificationFailure {
  switch (code) {
    case 'auth/invalid-phone-number':
    case 'auth/missing-phone-number':
      return 'invalid_number';
    case 'auth/invalid-verification-code':
    case 'auth/missing-verification-code':
      return 'invalid_code';
    case 'auth/code-expired':
    case 'auth/invalid-verification-id':
    case 'auth/session-expired':
      return 'code_expired';
    case 'auth/too-many-requests':
    case 'auth/quota-exceeded':
      return 'too_many_requests';
    case 'auth/operation-not-allowed':
    case 'auth/invalid-api-key':
    case 'auth/unauthorized-domain':
    case 'auth/app-not-authorized':
      return 'unavailable';
    case 'auth/captcha-check-failed':
    case 'auth/invalid-app-credential':
    case 'auth/missing-app-credential':
      return 'captcha';
    case 'auth/network-request-failed':
      return 'network';
    default:
      return 'unknown';
  }
}

export function phoneVerificationMessage(reason: PhoneVerificationFailure): string {
  switch (reason) {
    case 'invalid_number':
      return 'That mobile number does not look right. Check it and try again.';
    case 'invalid_code':
      return "That code isn't right. Check the SMS and try again.";
    case 'code_expired':
      return 'This code has expired. Send a new one.';
    case 'too_many_requests':
      return 'Too many attempts for this number. Wait a few minutes, then try again.';
    case 'unavailable':
      return "We can't send codes right now. Please try again later.";
    case 'captcha':
      return "We couldn't confirm this browser. Reload the page and try again.";
    case 'network':
      return "Can't reach the server. Check your connection and try again.";
    default:
      return 'Something went wrong sending the code. Please try again.';
  }
}

export function toVerificationError(error: unknown): PhoneVerificationError {
  if (error instanceof PhoneVerificationError) return error;
  const code =
    error && typeof error === 'object' && 'code' in error && typeof error.code === 'string'
      ? error.code
      : undefined;
  return new PhoneVerificationError(phoneVerificationFailure(code));
}

/** One Firebase app per config for the life of the page; `initializeAuth` may only run once per app. */
let cached: { key: string; auth: unknown } | null = null;

/**
 * `container` hosts the invisible reCAPTCHA Firebase requires before it sends an SMS. It must stay
 * mounted while the verifier is in use.
 */
export async function createWebPhoneVerifier(
  config: PhoneVerificationConfig,
  container: HTMLElement,
): Promise<PhoneVerifier> {
  const [{ getApps, initializeApp }, firebaseAuth] = await Promise.all([
    import('firebase/app'),
    import('firebase/auth'),
  ]);

  const key = `${config.projectId}:${config.appId}`;
  if (!cached || cached.key !== key) {
    const name = `signup-${config.projectId}`;
    const app =
      getApps().find((candidate) => candidate.name === name) ??
      initializeApp(
        {
          apiKey: config.apiKey,
          authDomain: config.authDomain,
          projectId: config.projectId,
          appId: config.appId,
        },
        name,
      );
    // In memory only: the Firebase session is a means to the token, never something to keep.
    const created = firebaseAuth.initializeAuth(app, {
      persistence: firebaseAuth.inMemoryPersistence,
    });
    created.useDeviceLanguage();
    cached = { key, auth: created };
  }
  const auth = cached.auth as ReturnType<typeof firebaseAuth.initializeAuth>;

  // Every send gets its own reCAPTCHA in its own fresh element: reusing one element (or one verifier)
  // across sends - or across the double effect run React makes in development - throws "reCAPTCHA
  // has already been rendered in this element". A duplicate call for the same number while a send is
  // in flight shares that send instead of starting a second one.
  const live = new Set<{ clear: () => void; host: HTMLElement }>();
  const releaseAll = () => {
    for (const entry of live) {
      try {
        entry.clear();
      } catch {
        // Already torn down with its element.
      }
      entry.host.remove();
    }
    live.clear();
  };
  let inFlight: { phone: string; promise: Promise<PhoneCodeSession> } | null = null;

  async function sendFresh(phone: string): Promise<PhoneCodeSession> {
    const host = document.createElement('div');
    container.appendChild(host);
    const recaptcha = new firebaseAuth.RecaptchaVerifier(auth, host, { size: 'invisible' });
    const entry = { clear: () => recaptcha.clear(), host };
    live.add(entry);
    let confirmation: Awaited<ReturnType<typeof firebaseAuth.signInWithPhoneNumber>>;
    try {
      confirmation = await firebaseAuth.signInWithPhoneNumber(auth, phone, recaptcha);
    } catch (error) {
      throw toVerificationError(error);
    } finally {
      // The code is on its way (or the attempt failed): the challenge has done its job either way.
      try {
        entry.clear();
      } catch {
        // Nothing left to clear.
      }
      host.remove();
      live.delete(entry);
    }
    return {
      async confirm(code) {
        try {
          const credential = await confirmation.confirm(code);
          const proof = await credential.user.getIdToken();
          await firebaseAuth.signOut(auth).catch(() => undefined);
          return proof;
        } catch (error) {
          throw toVerificationError(error);
        }
      },
    };
  }

  return {
    send(phone) {
      if (inFlight?.phone === phone) return inFlight.promise;
      const promise = sendFresh(phone);
      const entry = { phone, promise };
      inFlight = entry;
      const done = () => {
        if (inFlight === entry) inFlight = null;
      };
      promise.then(done, done);
      return promise;
    },
    dispose: releaseAll,
  };
}

/**
 * The verifier for this device: the phone's own Firebase SDK in the Android app (when the build has
 * it); in a browser on a company subdomain, the web SDK run from the app domain through a hidden
 * frame (Firebase refuses hosts it was not told about, and there is one per company —
 * `phoneVerifyFrame.ts`); the web SDK on this page everywhere else. `container` is only used by the
 * last one.
 */
export async function createPhoneVerifier(
  config: PhoneVerificationConfig,
  container: HTMLElement,
): Promise<PhoneVerifier> {
  const native = await import('./nativePhoneVerifier.js');
  if (native.nativePhoneAvailable()) return native.createNativePhoneVerifier();
  const framed = await import('./phoneVerifyFrame.js');
  // Never inside the Android app: its WebView fails the reCAPTCHA wherever it runs, and an app
  // without the native plugin keeps the behaviour it shipped with.
  if (framed.needsPhoneVerifyFrame() && !native.insideNativeApp()) {
    return framed.createFramedPhoneVerifier();
  }
  return createWebPhoneVerifier(config, container);
}

/** "+91 98765 •••10" — enough to recognise the number, not a copy of it on screen. */
export function maskPhone(e164: string, dialCode: string): string {
  const national = e164.startsWith(dialCode)
    ? e164.slice(dialCode.length)
    : e164.replace(/^\+/, '');
  if (national.length <= 4) return `${dialCode} ${national}`;
  const head = national.slice(0, Math.min(5, national.length - 4));
  const tail = national.slice(-2);
  const hidden = '•'.repeat(Math.max(1, national.length - head.length - tail.length));
  return `${dialCode} ${head} ${hidden}${tail}`;
}
